import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  /** Called with `false` when the user dismisses. Ignored while `loading`, so the dialog locks in flight. */
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (default true — this dialog exists for destructive actions). */
  destructive?: boolean;
  /** Hooks for browser-level QA; prefer accessible queries in unit tests. */
  testIds?: { content?: string; confirm?: string };
  loading?: boolean;
  onConfirm: () => void;
}

/**
 * The shared "are you sure?" dialog. Locked while the mutation is in flight (dismissal and both buttons),
 * so a double-confirm can't fire the mutation twice.
 *
 * Two APG behaviours are unconditional, because every use of this component is a confirmation prompt for
 * a consequential action — the case the APG names for both:
 * - `alertdialog`, so assistive tech announces the consequence, not just the title. Its precondition (an
 *   accessible description) is structural here: `description` is required.
 * - Focus opens on Cancel — "set focus on the least destructive action" when undoing is hard or
 *   impossible. Without it Radix focuses the corner ✕, and Enter-on-open would confirm.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  destructive = true,
  testIds,
  loading = false,
  onConfirm,
}: ConfirmDialogProps): React.ReactElement {
  const lockWhilePending = (event: Event | KeyboardEvent): void => {
    if (loading) event.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && loading) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        role="alertdialog"
        data-testid={testIds?.content}
        onEscapeKeyDown={lockWhilePending}
        onInteractOutside={lockWhilePending}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" disabled={loading} autoFocus onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'primary-destructive' : 'primary'}
            loading={loading}
            data-testid={testIds?.confirm}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
